import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { ChangeApprovalRecord } from './change-approval.config';
import {
  TransferOrderService,
  TransferOrderListVO,
  TransferOrderFilterDTO,
} from '@app/api/stock-service';

export interface ChangeApprovalSearchParams {
  transferOrderNumber?: string;
  status?: string;
  empathy?: string;
  moveOutWarehouse?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  size?: number;
}

export interface ChangeApprovalResponse {
  content: ChangeApprovalRecord[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

@Injectable({
  providedIn: 'root',
})
export class ChangeApprovalService {
  private readonly transferOrderService = inject(TransferOrderService);

  getChangeApprovalRecords(
    params: ChangeApprovalSearchParams,
  ): Observable<ChangeApprovalResponse> {
    const page = params.page ?? 0;
    const size = params.size ?? 20;
    const dto: TransferOrderFilterDTO = {
      code: params.transferOrderNumber,
      status: params.status
        ? [params.status as TransferOrderFilterDTO.StatusEnum]
        : undefined,
      startDate: params.startDate,
      endDate: params.endDate,
      page,
      size,
    };

    return this.transferOrderService.getPageInfo(dto).pipe(
      map((response) => ({
        content: (response.pageContent ?? []).map((vo) => this.mapRecord(vo)),
        totalElements: response.totalElements ?? 0,
        totalPages: response.totalPages ?? 0,
        size: response.size ?? size,
        number: response.number ?? page,
      })),
      catchError((err) => {
        console.error('Error fetching change approval records:', err);
        return of({
          content: [],
          totalElements: 0,
          totalPages: 0,
          size,
          number: page,
        });
      }),
    );
  }

  approveOrder(orderId: string): Observable<any> {
    return this.transferOrderService.updateStatus1(Number(orderId), 'APPROVED');
  }

  rejectOrder(orderId: string, _reason: string): Observable<any> {
    return this.transferOrderService.updateStatus1(
      Number(orderId),
      'CANCELLED',
    );
  }

  deleteChangeApprovalRecord(recordId: string): Observable<any> {
    return this.transferOrderService.updateStatus1(
      Number(recordId),
      'CANCELLED',
    );
  }

  private mapRecord(vo: TransferOrderListVO): ChangeApprovalRecord {
    return {
      id: String(vo.id ?? ''),
      transferOrderNumber: vo.code ?? '',
      moveOutWarehouse: String(vo.supportLogicWarehouseId ?? ''),
      empathy: '',
      transferQuantity: 0,
      remark: vo.description ?? '',
      status: vo.status ?? '',
      founder: '',
      creationTime: vo.creationDate ?? '',
    };
  }
}
